// A container's contents list: how an item is named, and how a bag's pockets break
// into the sections of the printed sheet.
//
// Shared, because the same list is now reached two ways — from the Inventory page by
// someone signed in, and from the public page a QR code on the bag opens (pages/
// KitList.jsx), which is served a narrowed payload by the backend and has no access
// to types, permissions or the rest of the inventory. Both build their sections here
// and hand them to the same exportContentsPdf(), so the sheet printed from the app and
// the sheet a scan produces cannot drift apart.
//
// Everything here works on *normalised rows*, the small shape both callers can produce:
//
//   { id, location_id, type_name, details, serial_number, size,
//     required_quantity, is_container }
//
// The public endpoint serves exactly that (see backend core/kit_qr.py); the Inventory
// page maps its loaded items and types into it — see normaliseItem() below.

// Natural, case-insensitive ordering, so "Bag 2" comes before "Bag 10".
export const cmpText = (a, b) =>
  (a || "").localeCompare(b || "", undefined, { numeric: true, sensitivity: "base" });

/**
 * An item's display name — its details (or its type's name when it has none), then
 * its serial number, then its size after a slash: "Response jacket 7 / Men M 48".
 *
 * `typeName` is passed in rather than looked up, since the two callers resolve the
 * type differently. Mirrored server-side by label() in core/inventory_items.py.
 */
export const itemLabel = (item, typeName) => {
  const named = [item.details || typeName, item.serial_number].filter(Boolean).join(" ");
  return [named, item.size].filter(Boolean).join(" / ") || "Item";
};

const rowLabel = (row) => itemLabel(row, row.type_name);

/**
 * Map a loaded inventory item into the shape above.
 *
 * The type name and whether the item is a container are passed in: the page asking
 * already resolves both off its own type list, and taking them as arguments keeps this
 * module free of the category constants (which live in constants.js and have to agree
 * with the backend enum).
 */
export const normaliseItem = (item, typeName, isContainer) => ({
  id: item.id,
  location_id: item.location_id ?? null,
  type_name: typeName ?? null,
  details: item.details ?? null,
  serial_number: item.serial_number ?? null,
  size: item.size ?? null,
  required_quantity: item.required_quantity ?? null,
  is_container: Boolean(isContainer),
});

/**
 * The printed contents list for one container, as a flat depth-first list of sections:
 * the items sitting loose in the container, then a section per pocket inside it, and so
 * on down. Nested containers *are* the sections, so a pocket isn't also listed as an
 * item of the bag holding it.
 *
 * `subtitle` names the pockets between a section and the container being printed, which
 * is why it stays empty for the container's own children — the page is already titled
 * after it. Required quantities only: the sheet is what should be packed, not what is.
 *
 * Returns `[{ title, subtitle, depth, items: [{ name, required }] }]`, which is what
 * exportContentsPdf() takes.
 */
export const contentsSections = (rows, rootId) => {
  const byParent = new Map();
  for (const row of rows) {
    const key = row.location_id ?? null;
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key).push(row);
  }
  // Always alphabetical, whichever sort the page behind it is using — the same fixed
  // order the contents viewer and the kit check present, which is what makes a printed
  // sheet followable against the bag.
  for (const list of byParent.values()) {
    list.sort((a, b) => cmpText(rowLabel(a), rowLabel(b)));
  }

  // A container cycle in the data would otherwise recurse forever. The authenticated
  // page guards its other walks the same way; here it also means a bad row can't hang
  // a page served to the public.
  const seen = new Set();

  const walk = (container, depth, trail) => {
    if (seen.has(container.id)) return [];
    seen.add(container.id);
    const inside = byParent.get(container.id) ?? [];
    const items = inside
      .filter(row => !row.is_container)
      .map(row => ({ name: rowLabel(row), required: row.required_quantity ?? null }));
    const nested = inside
      .filter(row => row.is_container)
      .flatMap(row => walk(row, depth + 1, depth === 0 ? [] : [...trail, rowLabel(container)]));
    // The outermost container only needs a section of its own when something sits loose
    // in it — otherwise its name is on the page already and the pockets speak for it.
    const own = depth === 0 && items.length === 0
      ? []
      : [{ title: rowLabel(container), subtitle: trail.join(" › "), depth, items }];
    return [...own, ...nested];
  };

  const root = rows.find(row => row.id === rootId);
  return root ? walk(root, 0, []) : [];
};

// --- Where a pocket sits in the bag ------------------------------------------------
// Pockets are placed by reading their own names, which is how the printed original is
// arranged: "Lid Pocket" is the top row, then "Top Left" beside "Top Right", down to
// "Bottom Left / Bottom Central / Bottom Right". A pocket naming no row keeps a place
// of its own after the grid, so a container that doesn't follow the convention simply
// reads as a list.
//
// Shared, because the order is a fact about the bag rather than about the medium: the
// PDF lays it out as a grid, the public page stacks it top to bottom (side-by-side
// columns don't fit a phone), and both must present the pockets in the order someone
// working through the physical container meets them.
const BANDS = ["lid", "top", "middle", "bottom"];
const COLUMNS = [["left"], ["central", "centre", "center"], ["right"]];

// Whole words only: "Left Pocket" places a pocket, "Cleft" doesn't.
const hasWord = (text, word) => new RegExp(`\\b${word}\\b`, "i").test(text || "");
const indexOfWord = (title, groups) => {
  const i = groups.findIndex(g => (Array.isArray(g) ? g : [g]).some(w => hasWord(title, w)));
  return i === -1 ? null : i;
};

/**
 * Group a flat section list into the rows of the printed grid.
 *
 * A *cell* is one top-level pocket plus anything nested inside it, kept together so a
 * pouch stays with the pocket holding it. A *row* is the cells sharing a band, ordered
 * left to right. Items sitting loose in the container itself (depth 0) lead.
 *
 * Returns `[[cell, …], …]` where a cell is `[section, …]` — the PDF draws each row as a
 * row of columns; `orderedSections()` below just flattens it.
 */
export const contentsRows = (sections) => {
  const rows = sections.filter(s => s.depth === 0).map(s => [[s]]);

  const cells = [];
  for (const s of sections) {
    if (s.depth === 0) continue;
    if (s.depth === 1 || cells.length === 0) cells.push([s]);
    else cells[cells.length - 1].push(s);
  }

  const placed = cells.map(cell => ({
    cell,
    band: indexOfWord(cell[0].title, BANDS),
    column: indexOfWord(cell[0].title, COLUMNS),
  }));

  BANDS.forEach((_, band) => {
    const inBand = placed
      .filter(p => p.band === band)
      // A pocket naming a band but no side (just "Lid Pocket") leads its row.
      .sort((a, b) => (a.column ?? -1) - (b.column ?? -1));
    if (inBand.length) rows.push(inBand.map(p => p.cell));
  });

  // Unplaceable names keep the order they arrived in (alphabetical), after the grid.
  placed.filter(p => p.band === null).forEach(p => rows.push([p.cell]));

  return rows;
};

/**
 * The sections in the order someone reads the bag: loose items first, then lid, top,
 * middle, bottom, and left to right within each band, each pocket followed by whatever
 * is nested inside it.
 *
 * This is the printed sheet's own order flattened into one column — which is what a
 * phone needs, since three pockets side by side don't fit and stacking them
 * alphabetically would bear no relation to the container in the reader's hands.
 */
export const orderedSections = (sections) => contentsRows(sections).flat(2);

/**
 * The whole printed sheet for a scanned QR code: what exportContentsPdf() needs, and
 * what the public page renders on screen. `payload` is the response from
 * GET /kit-list/{token}.
 *
 * The sections come back in tree order (alphabetical within each container), exactly as
 * the Inventory page builds them — `orderedSections()` is what puts them in reading
 * order, and both the PDF and the public page apply it themselves.
 */
export const kitListSheet = (payload) => {
  const rows = payload?.rows ?? [];
  const root = rows.find(row => row.id === payload?.item_id);
  return {
    name: root ? rowLabel(root) : "Container",
    notes: payload?.notes ?? "",
    sections: contentsSections(rows, payload?.item_id),
  };
};
